import { useCallback } from 'react';
import { PostgrestError } from '@supabase/supabase-js';
import { supabaseData } from '../../utils/supabase-data-client';
import { NoteTemplate } from '../../types/notes';
import { useCacheStore } from './useCacheStore';
import { useCacheFirstQuery } from './useCacheFirstQuery';

interface UseNoteTemplatesReturn {
  templates: NoteTemplate[];
  loading: boolean;
  error: PostgrestError | null;
  refetch: () => Promise<void>;
}

/**
 * Cache-first full list of note templates, sorted by name.
 */
const useNoteTemplates = (): UseNoteTemplatesReturn => {
  const cache = useCacheStore();

  const cacheReader = cache.getNoteTemplates;

  const cacheFilter = useCallback(
    (items: NoteTemplate[]) =>
      [...items].sort((a, b) => a.template_name.localeCompare(b.template_name)),
    [],
  );

  const networkFetch = useCallback(async () => {
    const { data, error } = await supabaseData
      .from('note_templates')
      .select('*')
      .order('template_name');

    if (data) {
      cache.setNoteTemplates(data as NoteTemplate[]);
      return { data: data as NoteTemplate[], error };
    }

    return { data: data as NoteTemplate[] | null, error };
  }, [cache]);

  const { data, loading, error, refetch } = useCacheFirstQuery<NoteTemplate>({
    cacheReader,
    cacheFilter,
    networkFetch,
    queryKey: '__templates__',
  });

  return { templates: data ?? [], loading, error, refetch };
};

export default useNoteTemplates;
